/**
 * scheduleAssignments.ts
 * Frontend service for the EmployeeAssignment API (Phase 3, Item 1+6+8).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * No raw fetch() calls — see LESSONS-LEARNED.md for why direct fetch 404s in dev.
 *
 * Endpoints:
 *   GET    /api/schedule/employee-assignments              — list all (admin)
 *   GET    /api/schedule/employee-assignments/:key         — single
 *   GET    /api/schedule/employee-assignments/user/:userKey/week/:weekStart
 *                                                          — by user + week
 *   GET    /api/schedule/employee-assignments/task/:taskKey
 *                                                          — by task (skill-gate / commit flow)
 *   POST   /api/schedule/employee-assignments              — create single
 *   POST   /api/schedule/employee-assignments/bulk-from-task
 *                                                          — Phase 3 Item 6: batch assign from task
 *   PUT    /api/schedule/employee-assignments/:key         — update
 *   DELETE /api/schedule/employee-assignments/:key         — soft delete
 *   GET    /api/schedule/assignable-users                  — Phase 3 Item 6: users with Tier
 */

import apiClient from './apiClient';
import type {
  EmployeeAssignment,
  EmployeeAssignmentWithTask,
  CreateEmployeeAssignmentPayload,
  UpdateEmployeeAssignmentPayload,
} from '../types/schedule';
import type { TaskAssignee } from './scheduleTasks';

// ── Service functions ─────────────────────────────────────────────────────────

/**
 * Fetch a single employee assignment by its primary key.
 */
export async function fetchEmployeeAssignment(key: string): Promise<EmployeeAssignment> {
  const response = await apiClient.get<EmployeeAssignment>(
    `/schedule/employee-assignments/${key}`,
  );
  return response.data;
}

/**
 * Fetch all active assignments for a specific user in a specific week.
 * Primary source for the capacity heatmap cell.
 *
 * @param userKey    UUID of the AppUser.
 * @param weekStart  'YYYY-MM-DD' — must be a Saturday.
 */
export async function fetchAssignmentsByUserAndWeek(
  userKey: string,
  weekStart: string,
): Promise<EmployeeAssignmentWithTask[]> {
  const response = await apiClient.get<EmployeeAssignmentWithTask[]>(
    `/schedule/employee-assignments/user/${userKey}/week/${weekStart}`,
  );
  return response.data;
}

/**
 * Fetch the active assignees linked to a specific ScheduleTask, joined with
 * AppUser display fields (TaskAssignee shape — carries `displayName`).
 *
 * Used by the Phase-2 "Task Weekly Schedule" grid (Phase2WeeklyGrid) to render
 * each assignee's name + workload bar per week. Returns the SAME rows as
 * listTaskAssignees (the task-grid chips), so the weekly grid stays in parity
 * with the chips.
 *
 * BUG-2026-0099: the backend route `GET /employee-assignments/task/:taskKey`
 * was previously unregistered, so this call 404'd and the weekly grid showed no
 * names even though the same people appeared as chips. The route now exists.
 *
 * @param taskKey  UUID of the ScheduleTask.
 */
export async function fetchAssignmentsByTask(
  taskKey: string,
): Promise<TaskAssignee[]> {
  const response = await apiClient.get<TaskAssignee[]>(
    `/schedule/employee-assignments/task/${taskKey}`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Phase 3, Item 7 — Skill-gate types and mentor picker endpoint
// ---------------------------------------------------------------------------

/** A user blocked by the skill gate — returned in the 409 body. */
export interface SkillGateBlockedUser {
  userKey: string;
  displayName: string;
  /** null when the user has no SkillMatrix row for this task type. */
  level: number | null;
  /**
   * W2-8 (P-15): staffed week-starts (ISO Saturdays) with no L3+ mentor present.
   * Set when the gate ran per staffed week; the Mentor Required dialog names
   * these weeks. Absent on older responses / aggregate gate paths.
   */
  uncoveredWeeks?: string[];
}

/** Structured error thrown when bulkAssignFromTask or createEmployeeAssignment returns 409 skill_gate. */
export class SkillGateError extends Error {
  readonly blockedUsers: SkillGateBlockedUser[];
  readonly taskTypeKey: string | null;

  constructor(
    message: string,
    blockedUsers: SkillGateBlockedUser[],
    taskTypeKey: string | null,
  ) {
    super(message);
    this.name = 'SkillGateError';
    this.blockedUsers = blockedUsers;
    this.taskTypeKey = taskTypeKey;
  }
}

// ---------------------------------------------------------------------------
// Wave 3 (percent retirement, Q3B) — absence-day conflict
// ---------------------------------------------------------------------------

/**
 * Structured error thrown when a staffing write returns HTTP 409
 * `error: 'absence_day_conflict'` (code ABSENCE_DAY_CONFLICT) — the app's ONE
 * non-overridable staffing block: a task assigned onto a day the person has a
 * dated absence. There is NO confirm/bypass path (Q3B): callers must show the
 * server `message` VERBATIM in an error surface and never offer "Save anyway".
 *
 * (The old per-day-percent 409 `overbook` gate — and its OverbookConfirmDialog
 * — was retired by Wave-3 R1: same-day task stacking never warns anymore.)
 */
export class AbsenceDayConflictError extends Error {
  readonly code = 'ABSENCE_DAY_CONFLICT';

  constructor(message: string) {
    super(message);
    this.name = 'AbsenceDayConflictError';
  }
}

/**
 * Map a 409 `absence_day_conflict` axios error to the typed error (with the
 * server's human message verbatim); returns null for anything else.
 */
export function toAbsenceDayConflictError(err: unknown): AbsenceDayConflictError | null {
  const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
  if (
    axiosErr?.response?.status === 409 &&
    axiosErr.response.data?.error === 'absence_day_conflict'
  ) {
    const msg = typeof axiosErr.response.data.message === 'string'
      ? axiosErr.response.data.message
      : 'A person cannot be assigned work on a day off.';
    return new AbsenceDayConflictError(msg);
  }
  return null;
}

// ---------------------------------------------------------------------------
// P2-8 — scheduled-plan lock (scenario_locked 409)
// ---------------------------------------------------------------------------

/** Fallback copy when the server 409 carries no message (should not happen). */
const SCENARIO_LOCKED_FALLBACK =
  'This plan is locked (Submitted). Use "Reopen to Edit" to change it.';

/**
 * Structured error thrown when a staffing/schedule write returns HTTP 409
 * `code: 'scenario_locked'` — the scheduled-plan edit lock (2026-06-25):
 * a Submitted/Archived plan is locked; "Reopen to Edit" (or Duplicate)
 * reverses it. Carries the server's human message VERBATIM so every snackbar
 * that renders err.message shows the remedy instead of axios's raw
 * "Request failed with status code 409".
 */
export class ScenarioLockedError extends Error {
  readonly code = 'scenario_locked';

  constructor(message: string) {
    super(message);
    this.name = 'ScenarioLockedError';
  }
}

/**
 * Map a 409 `scenario_locked` axios error to the typed error (with the
 * server's human message verbatim); returns null for anything else.
 * The backend errorHandler ships { error: <message>, code: 'scenario_locked' }.
 */
export function toScenarioLockedError(err: unknown): ScenarioLockedError | null {
  const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
  if (
    axiosErr?.response?.status === 409 &&
    axiosErr.response.data?.code === 'scenario_locked'
  ) {
    const msg = typeof axiosErr.response.data.error === 'string'
      ? axiosErr.response.data.error
      : SCENARIO_LOCKED_FALLBACK;
    return new ScenarioLockedError(msg);
  }
  return null;
}

// ---------------------------------------------------------------------------
// G2-7 (audit #2) — non-blocking weekly-overload advisory on write success
// ---------------------------------------------------------------------------

/**
 * One overloaded (person, week): Σ task busy-days exceeds the week's working
 * days (e.g. three week-long tasks = 15 task-days in a 5-day week). Mirrors
 * backend overbookHelper.WeekOverloadAdvisory. NON-blocking — it rides the
 * 200/201 success bodies of the staffing writes so the UI can hint at the
 * moment of over-assignment instead of surprising the planner at submit time.
 */
export interface WeekOverloadAdvisory {
  userKey: string;
  displayName: string;
  /** ISO Saturday 'YYYY-MM-DD'. */
  weekStart: string;
  /** Σ task busy-days (existing + this write) in the week. */
  taskDays: number;
  /** Working days in the week (holiday-aware). */
  workingDays: number;
}

/**
 * Human copy for the advisory, suffix-ready for a success snackbar:
 * "Heads up — Sam now carries more task-days than the week holds (8 task-days
 * in a 4-day week)." Returns null when there is nothing to say.
 */
export function weekOverloadHint(
  advisories: WeekOverloadAdvisory[] | undefined | null,
): string | null {
  if (!advisories || advisories.length === 0) return null;
  const a = advisories[0];
  const more = advisories.length > 1 ? ` (+${advisories.length - 1} more week${advisories.length > 2 ? 's' : ''})` : '';
  return `Heads up — ${a.displayName} now carries more task-days than the week holds ` +
    `(${a.taskDays} task-days in a ${a.workingDays}-day week)${more}.`;
}

/** A mentor eligible for assignment (Level >= minLevel for the task type). */
export interface EligibleMentor {
  AppUserKey: string;
  DisplayName: string;
  Email: string;
  Level: number;
}

/**
 * Fetch users who are eligible to mentor on a given TaskType (Level >= minLevel).
 * Used by MentorRequiredModal to populate the inline picker.
 *
 * @param taskTypeKey  UUID of the TaskType.
 * @param minLevel     Minimum skill level required (default 3).
 */
export async function fetchEligibleMentors(
  taskTypeKey: string,
  minLevel = 3,
): Promise<EligibleMentor[]> {
  const response = await apiClient.get<EligibleMentor[]>(
    '/schedule/skill-matrix/eligible-mentors',
    { params: { taskTypeKey, minLevel } },
  );
  return response.data;
}

/**
 * Create a new EmployeeAssignment.
 * The API enforces: WeekStart is Saturday, exactly one of (ScheduleTaskKey,
 * AbsenceType, IndexAllocationKey) non-null (CK_EA_TaskAbsenceOrIndex,
 * migration 113 — index-allocation presence wiring lands in Phase 9 Item 9.3).
 * The server pins Percent=100 on write (Wave 3 — binary person-day model).
 *
 * @param data  Assignment payload.
 * @throws AbsenceDayConflictError  when a task lands on a day the person has a
 *                                  dated absence (Q3B — hard, no bypass).
 * @throws SkillGateError  when the user lacks sufficient skill and no mentor is present.
 */
export async function createEmployeeAssignment(
  data: CreateEmployeeAssignmentPayload,
): Promise<EmployeeAssignment & { weekOverloaded?: WeekOverloadAdvisory[] }> {
  try {
    // G2-7: the 201 body is the created row plus the additive weekOverloaded
    // advisory array (non-blocking — surface via weekOverloadHint).
    const response = await apiClient.post<EmployeeAssignment & { weekOverloaded?: WeekOverloadAdvisory[] }>(
      '/schedule/employee-assignments',
      data,
    );
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
    if (axiosErr?.response?.status === 409 && axiosErr.response.data?.error === 'skill_gate') {
      const d = axiosErr.response.data as {
        message: string;
        blockedUsers: SkillGateBlockedUser[];
        taskTypeKey: string | null;
      };
      throw new SkillGateError(d.message, d.blockedUsers, d.taskTypeKey);
    }
    // Generic backend validation (e.g. "No active MachineBookings found for this
    // task. Place the task on a machine first.") — surface the server's human
    // message instead of axios's opaque "Request failed with status code 400".
    const backendMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    if (typeof backendMsg === 'string' && backendMsg) throw new Error(backendMsg);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// P2-23 — transactional multi-week absence save
// ---------------------------------------------------------------------------

/** One per-week slice of a multi-week absence (clamped to its Sat–Fri week). */
export interface AbsenceWeekEntry {
  WeekStart: string;  // 'YYYY-MM-DD' Saturday
  StartDate: string;  // within [WeekStart, WeekStart+6]
  EndDate: string;    // same containment, >= StartDate
}

export interface CreateAbsenceBulkPayload {
  UserKey: string;
  AbsenceType: 'Vacation' | 'Sick' | 'FieldWork' | 'Other';
  Notes?: string | null;
  weeks: AbsenceWeekEntry[];
}

export interface CreateAbsenceBulkResponse {
  created: EmployeeAssignment[];
  /** Weeks skipped because an identical active absence row already exists. */
  skipped: Array<{ WeekStart: string; reason: string }>;
}

/**
 * Create ALL weeks of a multi-week absence in ONE backend transaction (P2-23).
 * Everything saves or nothing does — no silent partial weeks — and retrying
 * after a failure never duplicates already-recorded weeks (the server skips
 * them). The server pins Percent=100 and ScenarioKey='BASELINE' (absences are
 * scenario-independent facts — P2-19).
 */
export async function createAbsenceBulk(
  data: CreateAbsenceBulkPayload,
): Promise<CreateAbsenceBulkResponse> {
  try {
    const response = await apiClient.post<CreateAbsenceBulkResponse>(
      '/schedule/employee-assignments/absence-bulk',
      data,
    );
    return response.data;
  } catch (err: unknown) {
    // P2-8: absence rows are BASELINE-pinned so scenario_locked cannot fire
    // today, but unwrap defensively — a raw axios 409 must never reach a
    // snackbar as "Request failed with status code 409".
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    throw err;
  }
}

/**
 * Update an existing EmployeeAssignment.
 * Mutable fields: Status, Notes, AbsenceType, StartDate/EndDate.
 * Percent is PINNED to 100 server-side on task rows (Wave 3 binary model) —
 * the sliders/percent fields that used to call this are retired.
 * Structural fields (UserKey, WeekStart, ScheduleTaskKey) require delete + re-create.
 * A date change that lands a task on a day off returns 409 absence_day_conflict.
 */
export async function updateEmployeeAssignment(
  key: string,
  data: UpdateEmployeeAssignmentPayload,
): Promise<EmployeeAssignment & { weekOverloaded?: WeekOverloadAdvisory[] }> {
  try {
    // G2-7: the 200 body is the updated row plus the additive weekOverloaded
    // advisory array (populated only when a date change re-judged the week).
    const response = await apiClient.put<EmployeeAssignment & { weekOverloaded?: WeekOverloadAdvisory[] }>(
      `/schedule/employee-assignments/${key}`,
      data,
    );
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    throw err;
  }
}

/**
 * Soft-delete a single EmployeeAssignment.
 * Sets sysLastAction = 'delete'; the row is never physically removed.
 * A row inside a scheduled (Submitted) plan 409s scenario_locked — thrown as
 * ScenarioLockedError so the snackbar shows the "Reopen it" remedy (P2-8).
 */
export async function deleteEmployeeAssignment(key: string): Promise<void> {
  try {
    await apiClient.delete(`/schedule/employee-assignments/${key}`);
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Phase 3, Item 9 — Drag-extend endpoint
// ---------------------------------------------------------------------------

/**
 * Response shape from POST /api/schedule/employee-assignments/extend
 */
export interface ExtendAssignmentResponse {
  /** The updated original assignment(s). */
  updated: EmployeeAssignment[];
  /** Any new assignments created for subsequent weeks. */
  created: EmployeeAssignment[];
  /** G2-7 additive: non-blocking weekly-overload advisories for this write. */
  weekOverloaded?: WeekOverloadAdvisory[];
}

/**
 * Extend an assignment's EndDate, optionally chaining into subsequent weeks.
 *
 * Within the same week: updates EndDate on the existing row only.
 * Across week boundaries: caps the original at its week's Friday, then creates
 * one new EmployeeAssignment per subsequent week at the same TaskKey/
 * AbsenceType/Source/Status/ScenarioKey as the original (Percent pinned 100).
 *
 * Wave-3 R2 fix: this used to convert ANY 409 into the (now retired) overbook
 * error, so drag-extending onto a day off opened a futile "Save anyway" loop.
 * A 409 `absence_day_conflict` now throws the distinct typed error — the drag
 * path shows the server message verbatim and reverts (Q3B — no bypass).
 *
 * Wave-3 R3: the extend endpoint also runs the per-week mentor gate on the
 * newly-added weeks. A 409 `skill_gate` throws SkillGateError so the drag UI can
 * offer "Assign anyway" (advisory) — pass `{ confirmSkillGate: true }` to bypass.
 *
 * @param assignmentKey  Primary key of the EmployeeAssignment to extend.
 * @param newEndDate     Target end date 'YYYY-MM-DD'. May cross week boundaries.
 * @param opts           `{ confirmSkillGate }` — bypass the advisory mentor gate.
 * @throws AbsenceDayConflictError when the extension covers a day the person
 *                                 has a dated absence (hard, no bypass).
 * @throws SkillGateError when a junior would extend into weeks with no L3+ mentor.
 */
export async function extendAssignment(
  assignmentKey: string,
  newEndDate: string,
  opts?: { confirmSkillGate?: boolean },
): Promise<ExtendAssignmentResponse> {
  try {
    const response = await apiClient.post<ExtendAssignmentResponse>(
      '/schedule/employee-assignments/extend',
      { assignmentKey, newEndDate, confirmSkillGate: opts?.confirmSkillGate === true },
    );
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
    if (axiosErr?.response?.status === 409 && axiosErr.response.data?.error === 'skill_gate') {
      const d = axiosErr.response.data as {
        message: string;
        blockedUsers: SkillGateBlockedUser[];
        taskTypeKey: string | null;
      };
      throw new SkillGateError(d.message, d.blockedUsers, d.taskTypeKey);
    }
    // Generic backend validation (e.g. "No active MachineBookings found for this
    // task. Place the task on a machine first.") — surface the server's human
    // message instead of axios's opaque "Request failed with status code 400".
    const backendMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    if (typeof backendMsg === 'string' && backendMsg) throw new Error(backendMsg);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Phase 3, Item 6 — Assign-from-Task bulk endpoint
// ---------------------------------------------------------------------------

/**
 * One user to assign. `percent` is a legacy wire field the server pins to 100
 * (Wave 3 binary model — an assignment is presence, not a fraction). Callers
 * always send 100; the field stays only for wire-shape compatibility.
 */
export interface UserPercentEntry {
  userKey: string;
  percent: number;
}

export interface BulkAssignFromTaskPayload {
  scheduleTaskKey: string;
  /**
   * Per-machine staffing (079): bind the created rows to ONE machine of a split
   * task and span only that booking's dates. Omit for a legacy task-level assign
   * (MachineBookingKey = NULL) spanning the whole task.
   */
  machineBookingKey?: string | null;
  userPercents: UserPercentEntry[];
  /**
   * W2-8 (P-15, warn-only): when true, bypasses the advisory mentor gate —
   * the "Assign anyway" action in the Mentor Required dialog.
   */
  confirmSkillGate?: boolean;
  /**
   * FEAT-2026-0007 6C.1 — scenario context.
   * When provided, assignments are stamped with this ScenarioKey instead of 'BASELINE'.
   * Omit (or pass undefined) for the existing BASELINE flow.
   */
  scenarioKey?: string;
  /**
   * Per-week assignment (2026-06-23): when set to a BST Saturday the booking spans,
   * create rows for THAT week ONLY (instead of every week of the test). Omit to span
   * all weeks. Lets a planner staff one week of a multi-week test at a time.
   */
  weekStart?: string;
}

/**
 * Bulk-create EmployeeAssignment rows from a ScheduleTask.
 *
 * The backend resolves the task's MachineBooking date range, computes the
 * week-starts that span it, and inserts one assignment row per user per week,
 * all in a single transaction.
 *
 * When the skill gate fires (HTTP 409 skill_gate), throws SkillGateError.
 * When a task lands on an absence day (HTTP 409 absence_day_conflict), throws
 * AbsenceDayConflictError — hard, no bypass (Q3B).
 *
 * @param payload  scheduleTaskKey + users (percent always 100 — see UserPercentEntry).
 * @returns        { created, weekOverloaded } — the created rows plus the
 *                 non-blocking weekly-overload advisories (G2-7). The old
 *                 bare-array body is still accepted and normalized, so a
 *                 stale server never breaks callers.
 * @throws SkillGateError  when one or more users lack sufficient skill and no
 *                          mentor is present.
 * @throws AbsenceDayConflictError  when any covered day is a day off for a user.
 */
export interface BulkAssignFromTaskResult {
  created: EmployeeAssignment[];
  weekOverloaded: WeekOverloadAdvisory[];
}

export async function bulkAssignFromTask(
  payload: BulkAssignFromTaskPayload,
): Promise<BulkAssignFromTaskResult> {
  try {
    const response = await apiClient.post<
      EmployeeAssignment[] | { created: EmployeeAssignment[]; weekOverloaded?: WeekOverloadAdvisory[] }
    >(
      '/schedule/employee-assignments/bulk-from-task',
      payload,
    );
    // G2-7 shape normalization (see @returns above).
    if (Array.isArray(response.data)) {
      return { created: response.data, weekOverloaded: [] };
    }
    return {
      created: response.data.created ?? [],
      weekOverloaded: response.data.weekOverloaded ?? [],
    };
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    const absence = toAbsenceDayConflictError(err);
    if (absence) throw absence;
    const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
    if (axiosErr?.response?.status === 409 && axiosErr.response.data?.error === 'skill_gate') {
      const data = axiosErr.response.data as {
        message: string;
        blockedUsers: SkillGateBlockedUser[];
        taskTypeKey: string | null;
      };
      throw new SkillGateError(data.message, data.blockedUsers, data.taskTypeKey);
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Phase 3, Item 6 — Assignable users list (with Tier for picker)
// ---------------------------------------------------------------------------

export interface AssignableUser {
  AppUserKey: string;
  DisplayName: string;
  Email: string;
  /** e.g. 'Lead', 'Senior', 'Tech', 'Junior' — null if not yet set */
  Tier: string | null;
  WorkGroup: string | null;
  JobClass: string | null;
  /**
   * BUG-2026-0090 — the user's level (1–5) for the task type's REQUIRED SKILL
   * (Lab Taxonomy 2026: resolved via TaskType.SkillKey on the server). Only
   * populated when listAssignableUsers is called with a taskTypeKey. null means
   * no recorded level for the required skill; undefined means the list was
   * loaded without a task-type filter (skill level not requested).
   */
  SkillLevel?: number | null;
  /**
   * Lab Taxonomy 2026 — false when the task type requires NO skill
   * (TaskType.SkillKey is NULL): qualification and mentor gating do not apply.
   * Only populated alongside SkillLevel.
   */
  SkillRequired?: boolean;
}

/**
 * Fetch all active, assignable users with their scheduling profile fields.
 * Used by AssignEmployeesDialog and InlineAssigneePicker to populate the picker.
 *
 * @param taskTypeKey  Optional. When provided, each returned user carries a
 *                     `SkillLevel` (1–5 | null) = their SkillMatrix level for
 *                     that task type, so the picker can show skill beside names
 *                     (BUG-2026-0090). Omit to get the plain list.
 */
export async function listAssignableUsers(
  taskTypeKey?: string | null,
): Promise<AssignableUser[]> {
  const response = await apiClient.get<AssignableUser[]>(
    '/schedule/assignable-users',
    taskTypeKey ? { params: { taskTypeKey } } : undefined,
  );
  return response.data;
}
